import { useSupabaseUser } from '~/composables/useSupabaseUser'
import { getMfaInfo, type ExtendedUserAccountSession, type PendingUser } from '~/repositories/mfaInfoRepository'

type MfaInfoState = {
  extendedUserAccountSession: ExtendedUserAccountSession | null
  mfaEnabled: boolean
  pendingUser: PendingUser | null
}

// セッション情報（AAL状態）・組織のMFA設定・初期設定未実施ユーザー情報をそれぞれ取得するEdge Functionを1つに統合した影響で
// composableもuseMfaInfoとして1つにまとめている
// composableレイヤでのみ分割する方法もあるが結局各composableでのロードをする必要があるのでcomposableとしても1つにまとめている
// (useSWRのdedupeのような機能があればcomposableを分割することで管理しやすくなりそうだが、Edge functionがPOSTリクエストなので現状キャッシュ系は厳しそう)
export const useMfaInfo = () => {
  const supabaseUser = useSupabaseUser()

  // undefinedはデータ取得前またはエラー時
  const state = useState<MfaInfoState | undefined>('useMfaInfo', () => undefined)

  // aalはsupabaseUserとextendedUserAccountSessionから算出
  const aal = computed<'unauthenticated' | 'aal1' | 'aal2' | null>(() => {
    if (!supabaseUser.value) {
      return 'unauthenticated'
    }
    if (state.value === undefined) {
      // まだrefreshされていない時も暫定的にaal1
      // (だがsupabaseUser.valueがある時点でロード済みのはずなのでここには到達しない想定)
      return 'aal1'
    }
    if (state.value.extendedUserAccountSession === null) {
      // refresh済みだがextendedUserAccountSessionがない = aal1
      return 'aal1'
    }
    // extendedUserAccountSessionがある場合はそのaalを返す
    return state.value.extendedUserAccountSession.aal
  })

  // MFA要件を満たしているかどうか
  const hasRequiredAal = computed(() => {
    // 未ログイン（一段階目もログインしていない）の場合はfalse
    // ロード前（!aal.value)もfalse側に倒している
    if (!aal.value || aal.value === 'unauthenticated') {
      return false
    }

    // 組織でのMFA設定がまだ取得できていない場合は、要件未充足扱い
    if (state.value === undefined) {
      return false
    }

    // 組織でMFAが無効なら常に許可
    if (state.value.mfaEnabled === false) {
      return true
    }

    // MFA有効なら、aal2が必要
    return aal.value === 'aal2'
  })

  // セッションに紐づくメールアドレス
  const sessionEmail = computed(() => {
    return supabaseUser.value?.email ?? null
  })

  // ユーザーアカウントID
  const userAccountId = computed(() => {
    return supabaseUser.value?.id ?? null
  })

  const refresh = async () => {
    if (supabaseUser.value) {
      try {
        const mfaInfo = await getMfaInfo()
        state.value = {
          extendedUserAccountSession: mfaInfo.extendedUserAccountSession,
          mfaEnabled: mfaInfo.organizationSetting.mfaEnabled,
          pendingUser: mfaInfo.pendingUser,
        }
      } catch (error) {
        state.value = undefined
        throw error
      }
    } else {
      state.value = undefined
    }
  }

  return {
    aal: readonly(aal),
    organizationMfaEnabled: computed(() => state.value?.mfaEnabled),
    pendingUser: computed(() => (state.value === undefined ? undefined : state.value.pendingUser)),
    sessionEmail,
    userAccountId,
    hasRequiredAal,
    refresh,
  }
}
